import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './SubNavigation.module.scss';
import { Icon, Link } from 'ui/components';
import ChevronIconNav from 'ui/icons/icon-chevron-nav.svg';
import { Button, Paragraph } from 'ui/components/1-atoms';

export interface SubNavigationProps {
	root: Navigation.NestedNavigationItem;
	onLeafNavigate?: () => void;
}

export const SubNavigation: React.FC<SubNavigationProps> = ({ root, onLeafNavigate }) => {
	type NavigationNode = Navigation.NestedNavigationItem;

	const [navigationStack, setNavigationStack] = useState<NavigationNode[]>([root]);

	const canNavigateBack = navigationStack.length > 1;

	const enterChildNode = (childNode: NavigationNode) => {
		if (childNode.decendants?.length) {
			setNavigationStack((previousStack) => [...previousStack, childNode]);
		}
	};

	const navigateBack = () => {
		if (navigationStack.length > 1) {
			setNavigationStack((previousStack) => previousStack.slice(0, -1));
		}
	};

	const visiblePanels = useMemo(() => navigationStack.map((node) => node), [navigationStack]);

	return (
		<div className={styles.SubNavigation} data-depth={navigationStack.length - 1}>
			<div className={styles.SubNavigation_header}>
				{canNavigateBack ? (
					<div className={styles.SubNavigation_headerRow}>
						<Button className={styles.SubNavigation_back} onClick={navigateBack} aria-label="Back">
							<Icon size="lg" className={styles.SubNavigation_backIcon}>
								<ChevronIconNav />
							</Icon>
							<span className={styles.SubNavigation_currentText}>Back</span>
						</Button>
					</div>
				) : null}
			</div>

			<div
				className={styles.SubNavigation_track}
				style={{ transform: `translateX(-${(navigationStack.length - 1) * 100}%)` }}
				aria-live="polite"
			>
				{visiblePanels.map((panelNode, levelIndex) => (
					<ul key={levelIndex} className={styles.SubNavigation_panel} role="menu">
						{levelIndex > 0 && panelNode.url ? (
							<li
								key={`__parent-${levelIndex}`}
								className={classNames(styles.SubNavigation_item, styles.SubNavigation_itemParent)}
								role="none"
							>
								<Link
									url={panelNode.url}
									target={panelNode.target}
									className={classNames(styles.SubNavigation_link, styles.SubNavigation_parentLinkInList)}
									handleClick={onLeafNavigate}
								>
									{panelNode.title ?? panelNode.name}
								</Link>
							</li>
						) : null}

						{(panelNode.decendants ?? []).map((childItem, itemIndex) => {
							const itemTitle = childItem.title ?? childItem.name;
							const hasChildren = (childItem.decendants?.length ?? 0) > 0;

							return (
								<li key={itemIndex} className={styles.SubNavigation_item} role="none">
									<div className={styles.SubNavigation_row}>
										{hasChildren ? (
											<>
												<Button
													className={classNames(styles.SubNavigation_link, styles.SubNavigation_button)}
													onClick={() => enterChildNode(childItem)}
													aria-haspopup="menu"
													aria-label={`Open ${itemTitle}`}
												>
													<span className={styles.SubNavigation_text}>{itemTitle}</span>
													{childItem.amount ? (
														<span className={styles.SubNavigation_amount}> ({childItem.amount})</span>
													) : null}
												</Button>

												<Button
													className={styles.SubNavigation_next}
													aria-label={`Open ${itemTitle}`}
													onClick={() => enterChildNode(childItem)}
												>
													<Icon size="lg" className={styles.SubNavigation_nextIcon}>
														<ChevronIconNav />
													</Icon>
												</Button>
											</>
										) : (
											<Link
												url={childItem.url}
												target={childItem.target}
												className={styles.SubNavigation_link}
												handleClick={onLeafNavigate}
											>
												{itemTitle}
												{childItem.amount ? <span className={styles.SubNavigation_amount}> ({childItem.amount})</span> : null}
											</Link>
										)}
									</div>

									{childItem.description ? (
										<Paragraph className={styles.SubNavigation_desc} style="sm">
											{childItem.description}
										</Paragraph>
									) : null}
								</li>
							);
						})}
					</ul>
				))}
			</div>
		</div>
	);
};
